/**
 * Playback hook. Animates the scene linearly between consecutive frames.
 *
 * While playing, returns an interpolated `Scene` where each token is
 * positioned along the line from its starting frame to the next frame.
 * Tokens, shapes, texts and strokes that exist in BOTH frames are matched by
 * id and tweened (only positions tween for now). Elements that exist only in
 * one frame snap-cut to the next frame.
 *
 * Playback advances `frameIndex` via `onAdvance` when each transition
 * completes, so the editor’s frame strip stays in sync.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { BoardSnapshot, Frame, Scene, Token } from "./scene.js";

interface UsePlaybackParams {
  snapshot: BoardSnapshot;
  currentIndex: number;
  onAdvance: (nextIndex: number) => void;
  /** Speed multiplier (1 = real-time, 2 = double speed). */
  speed?: number;
}

export interface UsePlayback {
  playing: boolean;
  /** Live progress in [0,1] for the *current* transition; only meaningful while playing. */
  progress: number;
  /** The scene to render — interpolated while playing, otherwise the current frame. */
  scene: Scene;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  stop: () => void;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linearly interpolate two scenes. Matches elements by id. */
function interpolateScene(a: Scene, b: Scene, t: number): Scene {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const bTokens = new Map<string, Token>(b.tokens.map((tk) => [tk.id, tk]));
  const tokens: Token[] = a.tokens.map((tk) => {
    const next = bTokens.get(tk.id);
    if (!next) return tk;
    return { ...tk, x: lerp(tk.x, next.x, t), y: lerp(tk.y, next.y, t) };
  });
  // Tokens that only appear in frame B: snap-in at t=1 only (already handled)
  // Strokes/shapes/texts: keep frame A's set while transitioning (cuts to B at t≥1)
  return {
    tokens,
    strokes: a.strokes,
    shapes: a.shapes,
    texts: a.texts,
  };
}

export function usePlayback({
  snapshot,
  currentIndex,
  onAdvance,
  speed = 1,
}: UsePlaybackParams): UsePlayback {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTsRef = useRef<number | null>(null);
  // Stable refs for animation loop without re-subscribing every render
  const stateRef = useRef({ snapshot, currentIndex, onAdvance, speed });
  stateRef.current = { snapshot, currentIndex, onAdvance, speed };

  const stop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startTsRef.current = null;
    setPlaying(false);
    setProgress(0);
  }, []);

  const tick = useCallback((ts: number) => {
    const { snapshot: snap, currentIndex: idx, onAdvance: advance, speed: sp } = stateRef.current;
    const frames = snap.frames;
    const target = frames[idx + 1];
    if (!target) {
      // No more frames — stop at end
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startTsRef.current = null;
      setPlaying(false);
      setProgress(0);
      return;
    }
    if (startTsRef.current == null) startTsRef.current = ts;
    const duration = Math.max(100, target.durationMs) / Math.max(0.1, sp);
    const elapsed = ts - startTsRef.current;
    const t = Math.min(1, elapsed / duration);
    setProgress(t);
    if (t >= 1) {
      // Advance to the next frame
      startTsRef.current = null;
      setProgress(0);
      advance(idx + 1);
      if (idx + 1 >= frames.length - 1) {
        // Last frame reached → stop
        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        setPlaying(false);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const play = useCallback(() => {
    const { snapshot: snap, currentIndex: idx, onAdvance: advance } = stateRef.current;
    if (snap.frames.length < 2) return;
    // If we're at the very last frame, rewind to start
    if (idx >= snap.frames.length - 1) {
      advance(0);
    }
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    startTsRef.current = null;
    setPlaying(true);
    setProgress(0);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const pause = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    startTsRef.current = null;
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (playing) pause();
    else play();
  }, [playing, pause, play]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const scene = (() => {
    const current = snapshot.frames[currentIndex];
    const next = snapshot.frames[currentIndex + 1];
    if (!current) return { tokens: [], strokes: [], shapes: [], texts: [] };
    if (!playing || !next) return current.scene;
    return interpolateScene(current.scene, next.scene, progress);
  })();

  return { playing, progress, scene, play, pause, toggle, stop };
}

/** Compute Laufweg trails: for every token present in BOTH `current` and any
 * other frame in `frames`, return polylines of its positions across all
 * frames. Skips the trail for tokens not present everywhere; tokens that
 * appear/disappear mid-sequence simply have shorter trails. */
export interface TokenTrail {
  tokenId: string;
  kind: Token["kind"];
  /** Flat [x0,y0,x1,y1,...] across frame positions in order. */
  points: number[];
}

export function computeTrails(frames: Frame[]): TokenTrail[] {
  if (frames.length < 2) return [];
  // Collect each token's position per frame in order
  const byId = new Map<string, { kind: Token["kind"]; points: number[] }>();
  for (const frame of frames) {
    for (const t of frame.scene.tokens) {
      let entry = byId.get(t.id);
      if (!entry) {
        entry = { kind: t.kind, points: [] };
        byId.set(t.id, entry);
      }
      entry.points.push(t.x, t.y);
    }
  }
  const out: TokenTrail[] = [];
  for (const [tokenId, entry] of byId) {
    if (entry.points.length >= 4) {
      out.push({ tokenId, kind: entry.kind, points: entry.points });
    }
  }
  return out;
}
